type FooterLink = {
  label: string;
  href: string;
};

/**
 * Where the institutional rail sends a visitor who is finished with the page.
 *
 * Almost always back to shopping: `/bestsellers` is a path all four storefronts serve,
 * and it is the answer for the guarantee, the policies, the reviews and the rest.
 */
const BROWSE_MAGAZINES: FooterLink = {
  label: 'Browse Magazines',
  href: '/bestsellers',
};

/**
 * The answer on a page somebody opened because something had gone wrong. They are not
 * shopping; they are looking for an order they have already placed, and the button is
 * the shortest way to it.
 *
 * The query string is the whole address — `/customer` on its own is the account landing
 * page. The same url is already what the header's Orders item and the account rail use.
 */
const MY_ORDERS: FooterLink = {
  label: 'My Orders',
  href: '/customer?xCmd=orders',
};

/**
 * The pages that get the support wording, by the path the rail already knows them as.
 *
 * This is the list — adding a page here is the whole of giving it a different button,
 * and every path that is not here keeps Browse Magazines. Keyed by path rather than by
 * brand because a support page is one on every brand that publishes it, and because a
 * brand whose /contact has not moved to this rail yet takes the entry for free on the
 * day it does.
 *
 * The keys are PageRegistry's standardised slugs, the same spellings `Institutional/
 * menu.ts` lists and the same ones a page hands to InstitutionalLayoutPropsBuilder. A
 * path spelled differently in any of the three is not a broken link — the old urls all
 * 301 — it is a page this list can never match.
 */
const SUPPORT_PAGES: Record<string, FooterLink> = {
  '/contact': MY_ORDERS,
  '/help': MY_ORDERS,
};

/**
 * The button a page should draw, which is Browse Magazines unless the page is named above.
 *
 * Takes the path rather than reading the url because a server render has no url to read —
 * the same reason the rail is told which entry to highlight instead of working it out.
 */
export function footerLinkFor(activePath?: string): FooterLink {
  return (activePath && SUPPORT_PAGES[activePath]) || BROWSE_MAGAZINES;
}
